// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// The one-line description of a "Summarize this view" request — the bubble the user
// sees the instant they click the button.
//
// Two rules, both learned from a real export:
//   * never show a tile id. "Summarize this view — bi_sensitivity_analysis" is
//     internal vocabulary; the screen says "Sensitivity Analysis", so does this.
//   * name the controls that actually shaped the numbers. Sensitivity at Hotel level
//     and at Segment level, or at 45 DBA and 14 DBA, are different answers — a
//     history entry that cannot tell them apart is worse than no history.
//
// chat/view_insight.py::_view_question builds the same sentence server-side (it is
// what gets stored and what the model sees). Keep the two in step.

import { TILES } from "../../app/config";

const SEGMENT_LABELS = {
  market_segment: "market segment",
  top_market_segment: "top market segments",
  room_type: "room type",
  room_class: "room class",
  rate_plan: "rate plan",
  channel: "channel",
};

const DOW_ALL = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DOW_NAMES = {
  mon: "Mondays", tue: "Tuesdays", wed: "Wednesdays", thu: "Thursdays",
  fri: "Fridays", sat: "Saturdays", sun: "Sundays",
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Which CONTROLS each tile actually renders — transcribed from WorkspaceControls.js,
// which is the source of truth.
//
// Redux keeps every control's value whether or not the current tile shows it, so a
// `comp_stay_range` left behind by the Sensitivity tile made a RevPAR summary claim
// "vs Aug 2 – Sep 1, 2025" — a comparison window RevPAR Performance has no picker
// for. Naming a control the user cannot see is worse than staying silent: it sends
// them looking for it.
const TILE_CONTROLS = {
  segment: ["pickup", "pace", "market", "otb", "forecast", "pricing", "flights",
            "otb_v2_multi_segment", "bi_pace_comparison", "bi_pickup_analysis",
            "bi_sensitivity_analysis"],
  table_type: ["revpar_index"],
  dba: ["bi_sensitivity_analysis"],
  dow: ["bi_sensitivity_analysis"],
  comparison: ["bi_sensitivity_analysis", "pace"],
};
// DbaPicker also appears on these, but only while scopeType === "Dba".
const DBA_WHEN_DBA_SCOPE = ["compset", "market", "pace"];

function hasControl(tiles, control, scope) {
  const owners = TILE_CONTROLS[control] || [];
  if ((tiles || []).some((t) => owners.includes(t))) return true;
  if (control === "dba" && scope === "Dba") {
    return (tiles || []).some((t) => DBA_WHEN_DBA_SCOPE.includes(t));
  }
  return false;
}

/** Tile id -> the title shown on screen, falling back to a de-underscored id. */
export function tileTitle(id) {
  const t = TILES && TILES[id];
  if (t && t.title) return t.title;
  return String(id || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// "YYYY-MM-DD" -> {y, m, d}. Parsed by hand: new Date("2026-08-01") is UTC midnight
// and renders as Jul 31 for anyone west of Greenwich.
function parts(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
}

/** "Aug 1–31, 2026" · "Aug 28 – Sep 3, 2026" · "Dec 28, 2026 – Jan 3, 2027" */
export function dateSpan(from, until) {
  const a = parts(from), b = parts(until);
  if (!a) return "";
  if (!b) return `${MONTHS[a.m - 1]} ${a.d}, ${a.y}`;
  if (a.y !== b.y) {
    return `${MONTHS[a.m - 1]} ${a.d}, ${a.y} – ${MONTHS[b.m - 1]} ${b.d}, ${b.y}`;
  }
  if (a.m !== b.m) return `${MONTHS[a.m - 1]} ${a.d} – ${MONTHS[b.m - 1]} ${b.d}, ${b.y}`;
  if (a.d === b.d) return `${MONTHS[a.m - 1]} ${a.d}, ${a.y}`;
  return `${MONTHS[a.m - 1]} ${a.d}–${b.d}, ${b.y}`;
}

/** "mon,tue" -> "Mondays, Tuesdays". All seven days is not a filter, so: "". */
export function dowLabel(raw) {
  if (!raw) return "";
  const list = (Array.isArray(raw) ? raw : String(raw).split(","))
    .map((d) => String(d).trim().toLowerCase().slice(0, 3))
    .filter((d) => DOW_ALL.includes(d));
  if (!list.length || list.length === DOW_ALL.length) return "";
  return list
    .sort((x, y) => DOW_ALL.indexOf(x) - DOW_ALL.indexOf(y))
    .map((d) => DOW_NAMES[d])
    .join(", ");
}

/**
 * The full sentence, e.g.
 *   Summarize this view — Sensitivity Analysis for Refinery
 *     — by market segment, Aug 1–31, 2026, 45 DBA, vs Aug 2 – Sep 1, 2025
 */
export function describeView(view, propertyName) {
  const v = view || {};
  const titles = (v.tiles || []).map(tileTitle).filter(Boolean);
  let out = `Summarize this view — ${titles.length ? titles.join(", ") : "current view"}`;
  if (propertyName) out += ` for ${propertyName}`;

  // Only mention a control the tile on screen actually has.
  const tiles = v.tiles || [];
  const scope = v.scope;
  const bits = [];

  const grain = v.segmented ? SEGMENT_LABELS[v.segment_type] : null;
  if (grain && hasControl(tiles, "segment")) bits.push(`by ${grain}`);

  // grain words lead, then the range: "monthly, Aug 1, 2026 – Jul 31, 2027"
  const tt = String(v.table_type || "").trim().toLowerCase();
  if ((tt === "monthly" || tt === "daily") && hasControl(tiles, "table_type")) {
    bits.push(tt);
  }

  const span = dateSpan(v.date_from, v.date_until);
  if (span) bits.push(span);

  if (v.dba && hasControl(tiles, "dba", scope)) bits.push(`${v.dba} DBA`);

  const dow = dowLabel(v.dow);
  if (dow && hasControl(tiles, "dow")) bits.push(`${dow} only`);

  if (hasControl(tiles, "comparison")) {
    const comp = dateSpan(v.comp_date_from, v.comp_date_until);
    if (comp) bits.push(`vs ${comp}`);
  }

  return bits.length ? `${out} — ${bits.join(", ")}` : out;
}
